// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useGetCustomStages, useCreateStage, useMutation, useUpdateStage } from '@frontend/app/hooks';
import { Stage } from '@services/custom-stages/models';
import { CREATE_STAGE, UPDATE_STAGE } from '@frontend/app/queries';
import { StageType } from '@frontend/app/types/globalTypes';

interface Campaign {
  id: number;
  name: string;
}

interface Status {
  id: number;
  name: string;
  priority: number;
}

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'multiSelect' | 'yesNo' | 'checkbox' | 'date' | 'file' | 'url' | 'callToAction';
  required: boolean;
  options?: string[];
  description?: string;
  campaignOnly?: boolean;
  isMemberLevel?: boolean;
  isProgramLevel?: boolean;
}

interface Task {
  id: number;
  name: string;
  ctaLabel: string;
  creatorAccess: boolean;
  fields: FormField[];
  fieldSchemas?: any[];
}

interface CustomStagesContextType {
  stages: Stage[];
  selectedStageId: number | null;
  setSelectedStageId: (id: number | null) => void;
  selectedStage: Stage | null;
  updateStage: (stageId: number, updates: Partial<Stage>) => void;
  updateTaskSettings: (stageId: number, taskSettings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  } | null) => void;
  updateCreatorAccess: (stageId: number, creatorAccess: boolean) => void;
  isNewStage: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  createNewStage: (
    name: string,
    statuses: Array<{ name: string; priority: number }>,
    tasks: Array<{ name: string; ctaLabel: string; creatorAccess: boolean; fields: any[] }>,
    taskSettings?: {
      initialStatuses: Array<string>;
      finalStatus: string | null;
    }
  ) => Promise<void>;
  refetchStages: () => Promise<void>;
  handleSaveStage: (
    name: string,
    statuses: Array<{ id?: number; name: string; priority: number }>,
    tasks: Array<{ name: string; ctaLabel: string; creatorAccess: boolean; fields: any[] }>,
    taskSettings?: {
      initialStatuses: Array<{ id: number; name: string }>;
      finalStatus: { id: number; name: string } | null;
    }
  ) => Promise<void>;
  applyTemplate: (stageId: number, template: {
    name: string;
    description?: string;
    statuses: Array<{ name: string; bgColor?: string }>
  }) => void;
}

const CustomStagesContext = createContext<CustomStagesContextType | undefined>(undefined);

export const CustomStagesProvider: React.FC<Props> = ({ children, programId }) => {
  const [stages, setStages] = useState<Stage[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [createStage] = useMutation(CREATE_STAGE);
  const [updateStageMutation] = useMutation(UPDATE_STAGE);
  // const [updateStageMutation] = useUpdateStage();
  const isNewStage = selectedStageId === null;

  const { data, loading, error, refetch } = useGetCustomStages({
    variables: { programId: programId || 0 },
    skip: !programId
  });

  const refetchStages = async () => {
    if (programId) {
      await refetch();
    }
  };

  useEffect(() => {
    if (data?.getAllStages) {
      const mappedStages = data.getAllStages.map((stage) => ({
        ...stage,
        tasks: stage.tasks?.map((task) => ({
          ...task,
          fields: task.fieldSchemas?.map((schema) => ({
            id: String(schema.id),
            label: schema.name,
            type: mapFieldType(schema.type),
            required: true,
            description: schema.description || '',
            isMemberLevel: schema.isMemberLevel,
            isProgramLevel: schema.isProgramLevel,
            options: schema.metaData?.options || [],
          })) || []
        }))
      }));
      setStages(mappedStages);
    }
  }, [data]);

  useEffect(() => {
    if (programId) {
      refetch();
    }
  }, [programId, refetch]);

  const selectedStage = stages.find((stage) => stage.id === selectedStageId) ?? null;

  const updateStage = (stageId: number, updates: Partial<Stage>) => {
    setStages((prevStages) =>
      prevStages.map((stage) =>
        (stage.id === stageId ? { ...stage, ...updates } : stage)
      )
    );
    setHasUnsavedChanges(true);
  };

  const updateTaskSettings = (stageId: number, taskSettings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  } | null) => {
    setStages((prevStages) =>
      prevStages.map((stage) => {
        return stage.id === stageId ? { ...stage, taskSettings } : stage;
      })
    );
    setHasUnsavedChanges(true);
  };

  const updateCreatorAccess = (stageId: number, creatorAccess: boolean) => {
    setStages((prevStages) =>
      prevStages.map((stage) =>
        (stage.id === stageId ? {
          ...stage,
          tasks: stage.tasks.map((task, index) =>
            (index === 0 ? { ...task, creatorAccess } : task)
          )
        } : stage)
      )
    );
    setHasUnsavedChanges(true);
  };

  const createNewStage = async (
    name: string,
    statuses: Array<{ name: string; priority: number }> = [],
    tasks: Array<{ name: string; ctaLabel: string; creatorAccess: boolean; fields: any[] }> = [],
    taskSettings?: {
      initialStatuses: Array<string>;
      finalStatus: string | null;
    }
  ) => {
    if (!programId) {
      throw new Error('Program ID is required');
    }

    // Map fields to fieldSchemas format
    const mappedTasks = tasks.map((task) => ({
      name: task.name,
      ctaLabel: task.ctaLabel,
      creatorAccess: task.creatorAccess,
      fields: task.fields.map((field) => ({
        name: field.label,
        type: getBackendFieldType(field.type),
        description: field.description,
        isMemberLevel: field.isMemberLevel,
        isProgramLevel: field.isProgramLevel,
        metadata: field.options?.length ? { options: field.options } : undefined,
        // campaignOnly: field.campaignOnly
        // required: field.required,
      }))
    }));

    const response = await createStage({
      variables: {
        data: {
          name: name.trim(),
          programId,
          type: StageType.CUSTOM,
          showInteraction: false,
          statuses,
          tasks: mappedTasks,
          taskSettings: taskSettings || null
        }
      }
    });

    if (!response.data?.createStage) {
      throw new Error('Failed to create stage');
    }

    // After successful creation, refetch all stages
    await refetch();

    // Update local state after refetch
    setSelectedStageId(response.data.createStage.id);
    return response.data.createStage;
  };

  const updateExistingStage = async (
    stageId: number,
    name: string,
    statuses: Array<{ id?: number; name: string; priority: number }>,
    tasks: Array<{ name: string; ctaLabel: string; creatorAccess: boolean; fields: any[] }>,
    taskSettings?: {
      initialStatuses: Array<string>;
      finalStatus: string | null;
    } | null
  ) => {
    if (!programId) {
      throw new Error('Program ID is required');
    }

    // Clean statuses data by removing __typename and bgColor
    const cleanStatuses = statuses.map((status) => ({
      name: status.name,
      priority: status.priority,
      metadata: {
        isFinalStatus: status.metadata?.isFinalStatus,
        enableNotification: status.metadata?.enableNotification
      }
    }));

    try {
      const response = await updateStageMutation({
        variables: {
          id: stageId,
          data: {
            id: stageId,
            name,
            programId,
            type: StageType.CUSTOM,
            showInteraction: false,
            statuses: cleanStatuses,
            tasks, // Using tasks directly since they're already mapped
            taskSettings: taskSettings || null
          }
        }
      });

      if (response.data?.updateStage) {
        await refetchStages();
        return response.data.updateStage;
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      throw error;
    }
  };

  const handleSaveStage = async (
    name: string,
    statuses: Array<{ id?: number; name: string; priority: number }>,
    tasks: Array<{ name: string; ctaLabel: string; creatorAccess: boolean; fields: any[] }>,
    taskSettings?: {
      initialStatuses: Array<{ id: number; name: string }>;
      finalStatus: { id: number; name: string } | null;
    }
  ) => {
    try {
      // Get the current stage to ensure we have latest state
      const currentStage = stages.find((stage) => stage.id === selectedStageId);

      // Transform taskSettings to match API expectations
      let transformedTaskSettings = taskSettings ? {
        initialStatuses: taskSettings.initialStatuses.map((status) => status.name),
        finalStatus: taskSettings.finalStatus?.name || null
      } : null;

      // Only include taskSettings if we have both initialStatuses and finalStatus
      if (!transformedTaskSettings?.finalStatus || !transformedTaskSettings.initialStatuses?.length) {
        transformedTaskSettings = null;
      }

      // Merge current stage tasks with new field changes
      const mappedTasks = tasks.map((task, index) => {
        // Get corresponding task from current stage if it exists
        const currentTask = currentStage?.tasks?.[index];

        return {
          // Use current task's name and creatorAccess if available, otherwise use new values
          name: currentTask?.name || task.name,
          ctaLabel: currentTask?.ctaLabel || task.ctaLabel,
          creatorAccess: currentTask?.creatorAccess ?? task.creatorAccess,
          // Always use the new fields from the UI
          fields: task.fields.map((field) => ({
            name: field.label,
            type: getBackendFieldType(field.type),
            description: field.description,
            isMemberLevel: field.isMemberLevel,
            isProgramLevel: field.isProgramLevel,
            metadata: field.options?.length ? { options: field.options } : undefined,
          }))
        };
      });

      if (selectedStageId) {
        return updateExistingStage(selectedStageId, name, statuses, mappedTasks, transformedTaskSettings);
      } else {
        return createNewStage(name, statuses, mappedTasks, transformedTaskSettings);
      }
    } catch (error) {
      console.error('Error saving stage:', error);
      throw error;
    }
  };

  const applyTemplate = (stageId: number, template: {
    name: string;
    description?: string;
    statuses: Array<{ name: string; bgColor?: string }>
  }) => {
    // Map template statuses to stage format with proper IDs and priorities
    const mappedStatuses = template.statuses.map((status, index) => ({
      id: Date.now() + index, // Generate unique IDs
      name: status.name,
      priority: index + 1,
      bgColor: status.bgColor
    }));

    // Update the stages array with all template data
    setStages((prevStages) =>
      prevStages.map((stage) =>
        (stage.id === stageId ? {
          ...stage,
          name: template.name,
          description: template.description || stage.description,
          statuses: mappedStatuses,
          // Preserve existing tasks but update their settings if needed
          tasks: stage.tasks.map((task, index) => ({
            ...task,
            // Update task name if it's the first task (main task)
            name: index === 0 ? template.name : task.name,
          }))
        } : stage)
      )
    );

    // Mark changes as unsaved
    setHasUnsavedChanges(true);
  };

  const value = {
    stages,
    selectedStageId,
    setSelectedStageId,
    selectedStage,
    updateStage,
    updateTaskSettings,
    updateCreatorAccess,
    isNewStage,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    createNewStage,
    refetchStages,
    handleSaveStage,
    applyTemplate,
  };

  if (loading) return <div>Loading stages...</div>;
  if (error) return <div>Error loading stages: {error.message}</div>;

  return (
    <CustomStagesContext.Provider value={value}>
      {children}
    </CustomStagesContext.Provider>
  );
};

export function useCustomStages() {
  const context = useContext(CustomStagesContext);
  if (context === undefined) {
    throw new Error('useCustomStages must be used within a CustomStagesProvider');
  }
  return context;
}

function mapFieldType(backendType: string): FormField['type'] {
  const typeMap: Record<string, FormField['type']> = {
    'NUMBER': 'number',
    'TEXT': 'text',
    'SINGLE_SELECT': 'select',
    'MULTI_SELECT': 'multiSelect',
    'BOOLEAN': 'yesNo',
    'DATE': 'date',
    'UPLOAD': 'file',
    'URL': 'url',
    'CTA': 'callToAction'
  };
  return typeMap[backendType] || 'text';
}

function getBackendFieldType(frontendType: string): string {
  const typeMap: Record<string, string> = {
    'text': 'TEXT',
    'number': 'NUMBER',
    'select': 'SINGLE_SELECT',
    'multiSelect': 'MULTI_SELECT',
    'yesNo': 'BOOLEAN',
    'checkbox': 'BOOLEAN',
    'date': 'DATE',
    'file': 'UPLOAD',
    'url': 'URL',
    'callToAction': 'CTA'
  };
  return typeMap[frontendType] || 'TEXT';
}
